import React, { useState } from 'react';
import { Flex, Descriptions, DescriptionsProps, Tag, Button, App, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import usePage from '@/layout/hooks/usePage';
import { PageId } from '@/types/page';
import { OrderV2, OrderItemV2, OrderItemLocation } from '@/types/inventoryV2';
import { dateFormat } from '@/utils/format';
import dayjs from 'dayjs';
import { getUserAvatarTip } from '@/utils/user';
import WMSAvatar from '@/styles/WMSAvatar';
import { ChangeEnum, ConfirmedEnum } from '@/types/inventory';
import { EyeOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { orderPreOut } from '@/apis/inventoryv2';
import PreOutOrderPrint, { OutQtyObj } from '../PreOutOrderPrint';
import { EditOutlined } from '@ant-design/icons';
import { Requirement } from '@/types/requirement';
import useOrderPreOutLocationTypes from '@/hooks/useOrderPreOutLocationTypes';
import PrintInventoryV2, { PrintType } from '@/SupplierPages/Home/InventoryOrder/components/PrintInventoryV2';
import useSourceType from '@/hooks/useSourceType';
import ViewPackOrderButton from './ViewPackOrderButton';
import ViewShipmentOrderButton from './ViewShipmentOrderButton';
import CommonTable from "@/components/CommonTable";
import errorCodes from '@/apis/error';

interface OrderBlockProps {
  fetchType: "REQ" | "SALE" | "SHIPMENT";
  detail: OrderV2;
  itemColumns: ColumnsType<OrderItemV2>;
  onPreviewBarcode: (locations: OrderItemLocation[], productName: string) => void;
  isPrinting: boolean;
  onPrintingChange: (printing: boolean) => void;
  loading: boolean;
  preOutSucc: (order: OrderV2) => void;
  onOpenConfirmQuantity?: (orderId: string) => void;
  onConfirmQuantitySucc?: (order: OrderV2) => void;
  requirement?: Requirement;
  preOutQtyMap?: Record<string, OutQtyObj>;
  onPrintDtl?: (id: string) => void;
}

// 订单区块组件
const OrderBlock: React.FC<OrderBlockProps> = ({
  fetchType,
  detail,
  itemColumns,
  onPreviewBarcode,
  isPrinting,
  onPrintingChange,
  loading,
  preOutSucc,
  onOpenConfirmQuantity,
  onConfirmQuantitySucc,
  requirement,
  preOutQtyMap,
  onPrintDtl
}) => {
  const { t } = useTranslation();
  const { pageId } = usePage();
  const isFinanceSalesOrder = pageId === PageId.F_SALE_OFFICIAL || PageId.SALE_OFFICIAL;
  const [preLoading, setPreLoading] = useState<boolean>(false);
  const selectPreOutLocationTypes = useOrderPreOutLocationTypes();
  const { confirmTypes, opTypes, changeTypes, getOpType, getChangeType, getConfirmType } = useSourceType();

  // 分配预出库处理
  const handleAllocatePreOut = async () => {
    if (!detail?.id) return;
    setPreLoading(true)
    try {
      const locationTypes = await selectPreOutLocationTypes();
      if (!locationTypes) return;
      let order = await orderPreOut(detail.id, locationTypes);
      notification.success({
        placement: 'top',
        message: t('warehouse.allocatePreOutSuccess'),
      });
      preOutSucc({
        ...detail,
        items: detail.items.map(item => ({
          ...item,
          locations: order.items.find(oi => oi.productId === item.productId)?.locations || item.locations
        }))
      })
    } catch (err: any) {
      if (err?.code === 73010) {
        notification.error({
          message: t('inventory.proQtyFalse'),
          description: err?.extra?.map((i: { productId: string }) => i.productId)?.join(', ')
        })
      } else if ([73022, 73014].includes(err)) {
        notification.error({
          message: t('inventory.calcErr')
        })
      } else if ([73024, 73028].includes(err?.code)) {
        notification.error({
          message: t('warehouse.insufficientInventory'),
          description: err?.extra?.productCode
        })
      }
      notification.error({
        placement: 'top',
        message: errorCodes[err] || t('warehouse.allocatePreOutFail'),
      });
    } finally {
      setPreLoading(false)
    }
  };

  // 数量确认处理
  const handleQuantityConfirm = () => {
    if (!detail?.id) return;
    onOpenConfirmQuantity?.(detail.id);
  };

  // Map ChangeEnum to display values
  const isInboundType = detail?.type && [ChangeEnum.IN, ChangeEnum.PRE_IN].includes(detail.type);
  const ordertype = getChangeType(isInboundType ? ChangeEnum.IN : ChangeEnum.OUT);
  const opType = getOpType(detail?.opType);
  const confirmType = getConfirmType(detail?.confirmState);
  const orderType = getChangeType(detail?.type);

  // 定义描述信息
  const descriptionItems: DescriptionsProps["items"] = [
    {
      label: t('purchase.orderNumber'),
      children: detail?.number,
      span: 1,
    },
    {
      label: t('order.operator'),
      children: detail && (
        <WMSAvatar
          uniChar={detail?.creator?.id ?? "1"}
          avatarId={detail?.creator?.avatar ?? ""}
          creator={detail?.creator}
          alt={detail?.creator ? getUserAvatarTip(detail?.creator) : ''}
          size={24}
        />
      ),
      span: 1,
    },
    {
      label: t('order.oprtTime'),
      children: detail && dateFormat(dayjs(detail?.createdAt), true),
      span: 1,
    },
    {
      label: t('order.orderType'),
      span: 1,
      children: detail && (
        <Tag key={orderType?.label} color={orderType?.color}>{`${orderType?.label ?? ""}`}</Tag>
      ),
    }
  ];

  if (detail?.type && [ChangeEnum.IN, ChangeEnum.PRE_IN].includes(detail.type)) {
    descriptionItems.splice(1, 0, {
      label: t('warehouse.arrivalAt'),
      children: detail?.arrivalAt ? dateFormat(dayjs(detail.arrivalAt), true) : '-',
      span: 1,
    })
  }

  // 重新定义包含预览功能的列
  const tableColumns: ColumnsType<OrderItemV2> = itemColumns.map(col => {
    if (col.key === 'operation') {
      return {
        ...col,
        render: (_, record: OrderItemV2) => {
          return (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => {
                onPreviewBarcode(record.locations || [], record.product?.name || '');
              }}
            >
              {t('warehouse.viewBarcode')}
            </Button>
          );
        }
      };
    }
    return col;
  });

  return (
    <div style={{ marginBottom: 32 }}>
      {/* 头部 - 参考原Modal的Title */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
        paddingBottom: 12,
        borderBottom: '1px solid #f0f0f0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 500 }}>{detail?.number}</span>
          <Tag key={opType?.label} color={opType?.color}>{`${opType?.label ?? ""}`} {`${ordertype?.label ?? ""}`}</Tag>
          <span>{t('order.printCount')}: {detail.printCount}</span>
        </div>
        <Flex gap={12} align="center">
          {isFinanceSalesOrder && [ChangeEnum.OUT, ChangeEnum.PRE_OUT].includes(detail.type) && detail.confirmState === ConfirmedEnum.CONFIRM && (
            <>
              <ViewShipmentOrderButton orderId={detail.id} />
              <ViewPackOrderButton orderId={detail.id} />
            </>
          )}
          <Tag key={confirmType?.label} color={confirmType?.color}>{`${confirmType?.label ?? ""}`}</Tag>
          {fetchType === "REQ" && detail.confirmState !== ConfirmedEnum.CONFIRM && <Button
            type="default"
            icon={<EditOutlined />}
            onClick={handleQuantityConfirm}
            disabled={loading}
          >
            {t('order.quantityConfirm')}
          </Button>}
          {fetchType === "REQ" && <PreOutOrderPrint
            order={detail}
            disabled={loading || isPrinting}
            onStart={() => onPrintingChange(true)}
            onEnd={() => onPrintingChange(false)}
            requirement={requirement}
            preOutQtyMap={preOutQtyMap}
            onPrinted={() => onPrintDtl?.(detail.id)}
          />}
          {fetchType === "SALE" && <PrintInventoryV2
            selectedRows={detail.items.map(item => ({ ...item, order: { ...detail, items: [] } }))}
            disabled={loading || isPrinting}
            onStart={() => onPrintingChange(true)}
            onEnd={() => onPrintingChange(false)}
            printType={[ChangeEnum.OUT, ChangeEnum.PRE_OUT].includes(detail.type) ? PrintType.OUT : PrintType.NORMAL}
            showBottom onPrinted={() => onPrintDtl?.(detail.id)}
          />}
          {/* <Button
              type="primary"
              onClick={handleAllocatePreOut}
              disabled={loading}
              loading={preLoading}
            >
              {t('warehouse.allocatePreOut')}
            </Button> */}
        </Flex>
      </div>

      {/* 描述信息 */}
      <Descriptions bordered items={descriptionItems} layout="vertical" column={4} size="small" />

      {/* 订单详情表格 */}
      <div style={{ marginTop: 16 }}>
        <h4 style={{ marginBottom: 12 }}>{t('order.orderDetails')}</h4>
        <CommonTable
          columns={tableColumns}
          dataSource={detail.items || []}
          rowKey="id"
          pagination={false}
          virtual
          scroll={{ x: 1200, y: 400 }}
          bordered
          size="small"
        />
      </div>
    </div>
  );
};

export default OrderBlock;
